// useThreadMessagesQuery — load a single thread's full detail
// (including its message history). Disabled when no threadId is
// selected so the chat workspace can keep the hook mounted without
// firing a request before the user picks a thread.

import { useQuery, keepPreviousData, type UseQueryResult } from '@tanstack/react-query';
import { useThreadsApiClient } from '../api/useThreadsApiClient';
import { threadKeys } from './queryKeys';
import { toThreadDetailView } from './toThreadView';
import type { ThreadDetailView } from './types';

export function useThreadMessagesQuery(
  threadId: string | null | undefined,
): UseQueryResult<ThreadDetailView | null> {
  const client = useThreadsApiClient();
  const enabled = typeof threadId === 'string' && threadId.length > 0;
  return useQuery<ThreadDetailView | null>({
    queryKey: threadKeys.detail(threadId ?? ''),
    enabled,
    queryFn: async ({ signal }) => {
      const raw = await client.fetchDetail(threadId as string, signal);
      return toThreadDetailView(raw);
    },
    // Keep previously fetched data visible while the next fetch runs.
    // Combined with staleTime this means: first load shows instantly from
    // cache; invalidateQueries refreshes in the background with no spinner.
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    // activeRun drives whether a mount effect replays the run's full event
    // history (see useNativeAgenthubExternalRuntime's recoverActiveRunOnMount).
    // Up to 30s of staleTime is fine for the rest of this view, but a stale
    // activeRun there triggers a real replay of already-persisted content on
    // top of the hydrated transcript. Always revalidate on mount — cached
    // data still renders instantly via placeholderData, this only adds a
    // background refetch instead of silently trusting stale cache.
    refetchOnMount: 'always',
  });
}
